import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const INITIATOR_ROLES = ["superadmin", "admin", "manager", "finance", "accountant"];
const MODES = [["pg", "Payment gateway"], ["cash", "Cash"], ["upi", "UPI"]];
const STATUS_TONE = {
  pending_approval: "bg-[#FEF3C7] text-[#92400E]",
  approved: "bg-[#D4E0D1] text-[#1A3317]",
  rejected: "bg-[#F4C7B9] text-[#7A2E18]",
  processing: "bg-[#E5E7EB] text-[#374151]",
};
const today = () => new Date().toISOString().slice(0, 10);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const inputCls = "w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white focus:outline-none focus:border-[#DC2626]";

const RefundForm = ({ customerId, maxAmount, onDone }) => {
  const [f, setF] = useState({ amount: "", refund_mode: "cash", transaction_id: "", payment_date: today(), reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/crm/customers/${customerId}/wallet/refunds`, { ...f, amount: Number(f.amount) });
      toast.success("Refund request sent to Superadmin for approval");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const needsTxn = f.refund_mode !== "cash";
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="wallet-refund-form">
      <div className="text-xs text-[#5C5A55]">Available to refund: <b data-testid="wallet-refund-available">{inr(maxAmount)}</b>. The account stays active.</div>
      <label className="block text-xs font-semibold">Refund amount (₹)
        <input type="number" min="1" max={maxAmount} required value={f.amount} onChange={set("amount")} className={inputCls} data-testid="wallet-refund-amount" />
      </label>
      <div className="text-xs font-semibold">Refund mode
        <div className="flex gap-2 mt-1">
          {MODES.map(([v, l]) => (
            <button type="button" key={v} onClick={() => setF({ ...f, refund_mode: v })} data-testid={`wallet-refund-mode-${v}`}
              className={`px-3 h-8 rounded-full border text-xs transition-colors ${f.refund_mode === v ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#E5E2D9] hover:bg-[#FEF2F2]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <label className="block text-xs font-semibold">Transaction ID {needsTxn ? "*" : "(optional)"}
        <input required={needsTxn} value={f.transaction_id} onChange={set("transaction_id")} className={inputCls} data-testid="wallet-refund-txn-id" />
      </label>
      <label className="block text-xs font-semibold">Payment date
        <input type="date" required max={today()} value={f.payment_date} onChange={set("payment_date")} className={inputCls} data-testid="wallet-refund-date" />
      </label>
      <label className="block text-xs font-semibold">Reason
        <textarea required minLength={3} rows={2} value={f.reason} onChange={set("reason")} className={`${inputCls} h-auto py-1.5`} data-testid="wallet-refund-reason" />
      </label>
      <button disabled={busy} className="w-full h-9 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-bold disabled:opacity-60" data-testid="wallet-refund-submit">
        {busy ? "Submitting…" : "Submit for approval"}
      </button>
    </form>
  );
};

const RefundRow = ({ r, canApprove, onDecide }) => (
  <div className="px-5 py-2.5 flex items-start gap-3 text-xs" data-testid={`wallet-refund-row-${r.id}`}>
    <div className="flex-1 min-w-0">
      <div className="font-semibold">{inr(r.amount)} · {r.refund_mode_label}{r.transaction_id ? ` · ${r.transaction_id}` : ""}</div>
      <div className="text-[10px] text-[#8A8780]">Paid {r.payment_date} · by {r.requested_by_name} · {r.reason}</div>
      {r.approved_by_name && <div className="text-[10px] text-[#8A8780]">{r.status} by {r.approved_by_name}{r.decision_note ? ` · ${r.decision_note}` : ""}</div>}
    </div>
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${STATUS_TONE[r.status] || ""}`} data-testid={`wallet-refund-status-${r.id}`}>{r.status.replace("_", " ")}</span>
    {canApprove && r.status === "pending_approval" && (
      <div className="flex gap-1">
        <button onClick={() => onDecide(r, "approve")} className="px-2 h-7 rounded bg-[#15803D] text-white font-bold" data-testid={`wallet-refund-approve-${r.id}`}>Approve</button>
        <button onClick={() => onDecide(r, "reject")} className="px-2 h-7 rounded border border-[#7A2E18] text-[#7A2E18] font-bold" data-testid={`wallet-refund-reject-${r.id}`}>Reject</button>
      </div>
    )}
  </div>
);

const DecisionDialog = ({ decision, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [decision]);
  if (!decision) return null;
  const { r, action } = decision;
  const isReject = action === "reject";
  const submit = async () => {
    if (isReject && note.trim().length < 3) { toast.error("Please enter a rejection reason (min 3 characters)"); return; }
    setBusy(true);
    try {
      await api.post(`/crm/wallet/refunds/${r.id}/${action}`, { note: note.trim() });
      toast.success(isReject ? "Refund rejected" : `Refund approved · ${inr(r.amount)} debited from wallet`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wallet-refund-decision-dialog">
        <DialogHeader><DialogTitle>{isReject ? "Reject refund" : "Approve refund"} · {inr(r.amount)}</DialogTitle></DialogHeader>
        <div className="text-xs text-[#5C5A55]">{r.refund_mode_label}{r.transaction_id ? ` · Txn ${r.transaction_id}` : ""} · paid {r.payment_date} · {r.reason}</div>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} h-auto py-1.5`}
                  placeholder={isReject ? "Reason for rejecting (required)" : "Approval note (optional)"} data-testid="wallet-refund-decision-note" />
        <button onClick={submit} disabled={busy} data-testid="wallet-refund-decision-confirm"
                className={`h-9 rounded-md text-white text-sm font-bold disabled:opacity-60 ${isReject ? "bg-[#7A2E18]" : "bg-[#15803D]"}`}>
          {busy ? "Saving…" : isReject ? "Reject refund" : "Approve & debit wallet"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export const WalletRefunds = ({ customerId, balance, onChanged }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState(null);
  const role = user?.role;
  const canInitiate = INITIATOR_ROLES.includes(role);
  const canApprove = role === "superadmin";
  const load = useCallback(() => {
    api.get(`/crm/customers/${customerId}/wallet/refunds`).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [customerId]);
  useEffect(() => { if (canInitiate) load(); }, [canInitiate, load]);
  if (!canInitiate) return null;
  const pending = rows.filter(r => r.status === "pending_approval").reduce((s, r) => s + r.amount, 0);
  const available = Math.max(0, balance - pending);
  const decide = (r, action) => setDecision({ r, action });
  return (
    <div className="bg-white border-t border-[#F0EEE7]" data-testid="wallet-refunds">
      <div className="px-5 pt-3 pb-1 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">Wallet Refunds {pending > 0 && `· ${inr(pending)} awaiting approval`}</span>
        <button onClick={() => setOpen(true)} disabled={available <= 0} className="h-8 px-3 rounded-md border border-[#DC2626] text-[#DC2626] text-xs font-bold hover:bg-[#FEF2F2] disabled:opacity-40" data-testid="wallet-refund-open">Initiate refund</button>
      </div>
      {rows.length === 0 ? <div className="px-5 pb-3 text-xs text-[#8A8780] italic">No refunds yet.</div>
        : <div className="divide-y divide-[#F0EEE7]">{rows.map(r => <RefundRow key={r.id} r={r} canApprove={canApprove} onDecide={decide} />)}</div>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="wallet-refund-dialog">
          <DialogHeader><DialogTitle>Initiate wallet refund</DialogTitle></DialogHeader>
          <RefundForm customerId={customerId} maxAmount={available} onDone={() => { setOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
      <DecisionDialog decision={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); onChanged(); }} />
    </div>
  );
};
